import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium ' +
  'transition-[background-color,color,box-shadow,transform] duration-150 ease-out ' +
  'active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 ' +
  'dark:focus-visible:ring-offset-neutral-900 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800 ' +
    'dark:bg-brand-500 dark:hover:bg-brand-400 dark:active:bg-brand-300 dark:text-neutral-950',
  secondary:
    'bg-white text-neutral-800 border border-neutral-200 shadow-sm hover:bg-neutral-50 active:bg-neutral-100 ' +
    'dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-700 dark:active:bg-neutral-600',
  ghost:
    'bg-transparent text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 ' +
    'dark:text-neutral-300 dark:hover:bg-neutral-800 dark:active:bg-neutral-700',
  danger:
    'bg-danger-600 text-white shadow-sm hover:bg-danger-700 active:bg-danger-800 ' +
    'dark:bg-danger-500 dark:hover:bg-danger-400',
  link:
    'bg-transparent text-brand-600 hover:text-brand-700 underline-offset-2 hover:underline p-0 h-auto ' +
    'dark:text-brand-400 dark:hover:text-brand-300',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm',
  md: 'h-9 px-3.5 text-body',
  lg: 'h-11 px-5 text-body-lg',
  icon: 'h-9 w-9 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled,
    leftIcon,
    rightIcon,
    fullWidth,
    children,
    type = 'button',
    ...rest
  },
  ref
) {
  const isLinkLike = variant === 'link';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        base,
        variants[variant],
        !isLinkLike && sizes[size],
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2
          className={cn('animate-spin', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')}
          aria-hidden="true"
        />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

export default Button;
